import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import { addMilliseconds, differenceInMilliseconds, set } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { TimelineAdjustTimeService } from '../../services/timeline-adjust-time.service';
import { MoveShiftDropZoneService } from '../move-shift-drop-zone-v2/move-shift-drop-zone.service';
import { TimelineCellHighlightService } from '@wilson/shift-timeline/assignments-gantt-chart';

@Component({
  selector: 'wilson-move-shift-drop-zone-cell-view',
  imports: [CommonModule],
  template: `<div
    class="drop-zone"
    [style.left.px]="resourcesColumnWidthPx$ | async"
  ></div>`,
  styleUrls: ['./move-shift-drop-zone-cell-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttDateToPositionPxPipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    TimelineAdjustTimeService,
    MoveShiftDropZoneService,
  ],
})
export class MoveShiftDropZoneCellViewComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCellHighlightService = inject(
    TimelineCellHighlightService,
  );
  private readonly moveShiftDropZoneService = inject(MoveShiftDropZoneService);

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  async ngOnInit() {
    await this.moveShiftDropZoneService.setShiftWidth();
  }

  protected override async onDragOverHandler(offsetX: number) {
    this.timelineCellHighlightService.clearCellsFromHighlight();
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );

    if (draggingShift) {
      const dateFromPx = getDateFromPixelPosition({
        pixelPosition: offsetX,
        timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
      });
      const timeDifference = differenceInMilliseconds(
        new Date(draggingShift.endDatetime),
        new Date(draggingShift.startDatetime),
      );
      const newStartDate = set(dateFromPx, {
        hours: new Date(draggingShift.startDatetime).getHours(),
        minutes: new Date(draggingShift.startDatetime).getMinutes(),
      });
      const draggingMockShift = {
        ...draggingShift,
        startDatetime: newStartDate.toISOString(),
        endDatetime: addMilliseconds(
          newStartDate,
          timeDifference,
        ).toISOString(),
      };

      await this.moveShiftDropZoneService.onDragOverHandler(draggingMockShift);

      this.timelineCellHighlightService.setCellToHighlight(
        draggingMockShift.userId as string,
        draggingMockShift.startDatetime,
      );
    }
  }

  protected override async onDragLeaveHandler() {
    await this.moveShiftDropZoneService.onDragLeaveHandler();
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }

  protected override async onDropHandler() {
    await this.moveShiftDropZoneService.onDropHandler();
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }
}
